import { useState, useEffect } from "react"
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "../ui/avatar"
import { Progress } from "../ui/progress"
import { Badge } from "../ui/badge"
import { Input } from "../ui/input"
import { Search, ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "../ui/button"
import { adminApi, SolProgress as SolProgressType, PaginatedSolProgress } from "../../api/admin.api"

interface SolProgressProps {
    data: SolProgressType[] | PaginatedSolProgress
}

export function SolProgress({ data }: SolProgressProps) {
    const [searchTerm, setSearchTerm] = useState("")
    const [isLoading, setIsLoading] = useState(false)

    // Normalize data to handle both Array (legacy/simple) and Paginated (advanced)
    const initialList = Array.isArray(data) ? data : data?.data || [];
    const initialMeta = Array.isArray(data) ? null : data?.meta;

    const [items, setItems] = useState<SolProgressType[]>(initialList)
    const [meta, setMeta] = useState(initialMeta)

    // Update state if props change (initial load from parent)
    useEffect(() => {
        if (Array.isArray(data)) {
            setItems(data);
            setMeta(null);
        } else if (data) {
            setItems(data.data || []);
            setMeta(data.meta);
        }
    }, [data]);

    const fetchPage = async (page: number) => {
        setIsLoading(true);
        try {
            // Default limit 10 for pagination in this widget
            const limit = meta?.limit || 10;
            const res = await adminApi.getSolProgress(limit, page);
            if (res && res.data) {
                setItems(res.data);
                setMeta(res.meta);
            }
        } catch (error) {
            console.error("Failed to fetch page", error);
        } finally {
            setIsLoading(false);
        }
    }

    if (!items || items.length === 0) {
        return (
            <div className="flex h-[200px] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Aucune donnée disponible
            </div>
        )
    }

    const filteredData = items.filter(item =>
        item.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.compte_code.toLowerCase().includes(searchTerm.toLowerCase())
    )

    return (
        <div className="space-y-4">
            <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder="Rechercher un client ou code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                />
            </div>

            <div className={`space-y-8 max-h-[400px] overflow-y-auto pr-2 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                {filteredData.map((item) => (
                    <div key={item.compte_code} className="flex items-center">
                        <Avatar className="h-9 w-9">
                            <AvatarImage src={`https://api.dicebear.com/7.x/initials/svg?seed=${item.client_name}`} alt={item.client_name} />
                            <AvatarFallback>{item.client_name.slice(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="ml-4 space-y-1 flex-1">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium leading-none">
                                        {item.client_name}
                                        <span className="ml-1.5 text-xs text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                                            #{item.compte_code}
                                        </span>
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground hidden sm:inline">{item.plan}</span>
                                    <Badge variant={item.est_a_jour ? "outline" : "destructive"} className="text-[10px] px-1 py-0 h-5">
                                        {item.est_a_jour ? "À jour" : "En retard"}
                                    </Badge>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <Progress value={item.progress_percent} className="h-2" />
                                <span className="text-xs text-muted-foreground w-12 text-right">{item.progress_percent}%</span>
                            </div>

                            <div className="flex justify-between items-center text-xs text-muted-foreground">
                                <span>
                                    {item.semaines_payees} / {item.total_semaines} {item.frequence?.toLowerCase() || 'paiements'}
                                </span>
                                {(item.montant_non_paye || 0) > 0 && (
                                    <span className="text-red-500 font-medium">
                                        Reste: {(item.montant_non_paye || 0).toLocaleString()} {item.devise}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                ))}

                {filteredData.length === 0 && !isLoading && (
                    <div className="text-center py-8 text-sm text-muted-foreground">
                        Aucun résultat trouvé pour "{searchTerm}"
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {meta && meta.total > meta.limit && !searchTerm && (
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                    <div>
                        Page {meta.page} sur {meta.last_page || 1}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            disabled={meta.page <= 1 || isLoading}
                            onClick={() => fetchPage(meta.page - 1)}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7"
                            disabled={meta.page >= (meta.last_page || 1) || isLoading}
                            onClick={() => fetchPage(meta.page + 1)}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
